'use client';

import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table';
import {cva} from 'class-variance-authority';
import {createContext, useContext, useMemo, useState} from 'react';

import {PINNED_TABLE_CLASS, getPinnedClassName} from './pinning.utils';
import {cn} from '../../lib/utils';
import {
  Table as BaseTable,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../shadcn/table';

import type {
  ColumnDef,
  ColumnFiltersState,
  OnChangeFn,
  Row,
  SortingState,
  VisibilityState
} from '@tanstack/react-table';
import type {ReactNode} from 'react';

/**
 * App-level default for the `pinColumns` prop, which keeps the first column and a column with
 * id `actions` visible while a table scrolls sideways (pass `false` to opt one table out).
 * Pinned cells take their row's background, so row classes must be opaque.
 */
export const TablePinningContext = createContext(false);

// `embedded` sits flush in a parent card; its fixed layout keeps percentage widths aligned across tables.
export const tableVariantsConfig = {
  variants: {variant: {default: 'rounded-xl border shadow-xs', embedded: ''}},
  defaultVariants: {variant: 'default'}
} as const;

const tableFrameVariants = cva(
  'w-full overflow-x-auto border-border bg-background-surface',
  tableVariantsConfig
);

export interface TableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  emptyStateText?: ReactNode;
  emptyState?: ReactNode;
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  manualSorting?: boolean;
  footer?: ReactNode;
  onRowClick?: (row: TData) => void;
  getRowClassName?: (row: TData) => string | undefined;
  /**
   * Background class for a small start-edge accent bar overlaid on a row's first cell.
   * Rendered as an absolutely-positioned overlay (not a border) — a `<tr>`'s own border never
   * paints once `border-collapse: collapse` is active, and a border on the cell paints in a
   * separate layer from the row's hover background, leaving a gap in the hover fill. Return
   * `undefined` for rows that should show no bar at all.
   */
  getRowAccentClassName?: (row: TData) => string | undefined;
  pinColumns?: boolean;
  /** Rows this matches always render first, regardless of the active sort column. */
  isRowPinned?: (row: TData) => boolean;
  /**
   * Groups consecutive rows sharing the same key under a divider row rendered by
   * renderGroupHeader. Rows must already be sorted so same-key rows are contiguous —
   * this only detects boundaries, it does not re-sort or partition the data itself.
   */
  getGroupKey?: (row: TData) => string;
  /**
   * Returning nullish for a given boundary skips its divider row entirely — e.g. to
   * label only one group among several (the first group boundary always fires, even
   * when that group needs no visible header).
   */
  renderGroupHeader?: (row: TData, groupCount: number) => ReactNode;
  /** Rows this matches aren't rendered, grouped or not; in a group they still count toward its header. */
  isRowCollapsed?: (row: TData) => boolean;
  /** Rows hold a link stretched over them (`STRETCHED_OVERLAY_CLASS`): row hover styling without `role="button"`. */
  rowLink?: boolean;
  variant?: keyof (typeof tableVariantsConfig)['variants']['variant'];
  minWidth?: string;
}

export function Table<TData, TValue>({
  columns,
  data,
  emptyStateText,
  emptyState,
  sorting,
  onSortingChange,
  manualSorting = false,
  footer,
  onRowClick,
  getRowClassName,
  getRowAccentClassName,
  pinColumns: pinColumnsProp,
  isRowPinned,
  getGroupKey,
  renderGroupHeader,
  isRowCollapsed,
  rowLink = false,
  variant = 'default',
  minWidth
}: TableProps<TData, TValue>) {
  const pinColumnsDefault = useContext(TablePinningContext);
  const pinColumns = pinColumnsProp ?? pinColumnsDefault;
  const isSortingControlled = sorting !== undefined;
  const [internalSorting, setInternalSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

  const resolvedSorting = isSortingControlled ? sorting : internalSorting;
  const resolvedOnSortingChange: OnChangeFn<SortingState> = isSortingControlled
    ? updater => {
        if (onSortingChange) {
          onSortingChange(updater);
        }
      }
    : setInternalSorting;

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    ...(manualSorting ? {} : {getSortedRowModel: getSortedRowModel()}),
    getFilteredRowModel: getFilteredRowModel(),
    manualSorting,
    onSortingChange: resolvedOnSortingChange,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    state: {sorting: resolvedSorting, columnFilters, columnVisibility}
  });

  const sortedRows = table.getRowModel().rows;
  // Partition instead of relying on TanStack's rowPinning state, which renders
  // pinned rows in a fixed order and ignores the active sort within the pinned group.
  const rows = useMemo(() => {
    if (!isRowPinned) return sortedRows;
    const pinned = sortedRows.filter(row => isRowPinned(row.original));
    const unpinned = sortedRows.filter(row => !isRowPinned(row.original));
    return [...pinned, ...unpinned];
  }, [sortedRows, isRowPinned]);

  // Computed once per contiguous run (not a global tally), so rows relocated by
  // isRowPinned that split a group into two runs each get their own correct count
  // instead of the combined total.
  const groupBoundaries = useMemo(() => {
    if (!getGroupKey) return null;
    const startsGroup: boolean[] = [];
    const groupCounts: number[] = [];
    let runStart = 0;
    let runKey: string | undefined;
    rows.forEach((row, index) => {
      const key = getGroupKey(row.original);
      const isNewRun = index === 0 || key !== runKey;
      startsGroup.push(isNewRun);
      if (isNewRun) {
        runStart = index;
        runKey = key;
      }
      groupCounts[runStart] = (groupCounts[runStart] ?? 0) + 1;
    });
    return {startsGroup, groupCounts};
  }, [rows, getGroupKey]);

  const isInteractive = Boolean(onRowClick) || rowLink;

  const renderDataRow = (row: Row<TData>) => (
    <TableRow
      key={row.id}
      data-state={row.getIsSelected() ? 'selected' : undefined}
      onClick={onRowClick ? () => onRowClick(row.original) : undefined}
      className={cn(
        pinColumns && 'bg-background-surface',
        isInteractive && 'group/row cursor-pointer',
        rowLink && 'relative',
        isInteractive &&
          (pinColumns ? 'hover:bg-background-selected' : 'hover:bg-background-selected/50'),
        getRowClassName?.(row.original)
      )}
      role={onRowClick ? 'button' : undefined}
      tabIndex={onRowClick ? 0 : undefined}
      onKeyDown={
        onRowClick
          ? e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onRowClick(row.original);
              }
            }
          : undefined
      }
    >
      {row.getVisibleCells().map((cell, index) => {
        const accentClassName = index === 0 ? getRowAccentClassName?.(row.original) : undefined;
        const pinnedClassName = pinColumns
          ? getPinnedClassName(cell.column.id, index, false)
          : undefined;
        return (
          <TableCell
            key={cell.id}
            className={pinnedClassName ?? (accentClassName ? 'relative' : undefined)}
            style={{
              minWidth: cell.column.columnDef.meta?.minWidth,
              maxWidth: cell.column.columnDef.meta?.maxWidth
            }}
          >
            {accentClassName && (
              <span
                aria-hidden
                className={cn('absolute inset-y-0 start-0 w-[3px]', accentClassName)}
              />
            )}
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        );
      })}
    </TableRow>
  );

  // Grouped tables walk every row so headers and counts still see collapsed ones.
  const bodyRows: ReactNode[] =
    groupBoundaries && renderGroupHeader
      ? rows.flatMap((row, index) => {
          const groupHeaderContent = groupBoundaries.startsGroup[index]
            ? renderGroupHeader(row.original, groupBoundaries.groupCounts[index] ?? 1)
            : null;
          return [
            groupHeaderContent != null && (
              <TableRow key={`group-${row.id}`} className="hover:bg-transparent">
                <TableCell
                  colSpan={Math.max(columns.length, 1)}
                  className={cn(
                    'bg-background-secondary py-3',
                    index > 0 && !pinColumns && 'border-t-2 border-t-border'
                  )}
                >
                  {groupHeaderContent}
                </TableCell>
              </TableRow>
            ),
            !isRowCollapsed?.(row.original) && renderDataRow(row)
          ].filter(Boolean);
        })
      : rows.filter(row => !isRowCollapsed?.(row.original)).map(renderDataRow);

  return (
    <div data-slot="table" className={tableFrameVariants({variant})}>
      <BaseTable
        className={cn(pinColumns && PINNED_TABLE_CLASS, variant === 'embedded' && 'table-fixed')}
        style={minWidth ? {minWidth} : undefined}
      >
        <colgroup>
          {table.getAllColumns().map(column => {
            const w = column.columnDef.meta?.width;
            const colStyle = w === 'min' ? {width: '1px'} : w ? {width: w} : undefined;
            return <col key={column.id} {...(colStyle ? {style: colStyle} : {})} />;
          })}
        </colgroup>
        <TableHeader>
          {table.getHeaderGroups().map(headerGroup => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header, index) => (
                <TableHead
                  key={header.id}
                  className={
                    pinColumns ? getPinnedClassName(header.column.id, index, true) : undefined
                  }
                  style={{
                    minWidth: header.column.columnDef.meta?.minWidth,
                    maxWidth: header.column.columnDef.meta?.maxWidth
                  }}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {bodyRows.length === 0 ? (
            <TableRow className="hover:bg-transparent">
              <TableCell
                colSpan={Math.max(columns.length, 1)}
                className={emptyState ? 'p-0' : 'h-24 text-center text-fg-tertiary'}
              >
                {emptyState ?? emptyStateText}
              </TableCell>
            </TableRow>
          ) : (
            bodyRows
          )}
        </TableBody>
      </BaseTable>
      {footer}
    </div>
  );
}

export type {
  OnChangeFn as TableOnSortingChange,
  SortingState as TableSortingState
} from '@tanstack/react-table';
